import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { endsAfterStarts, type PatientResponse as Patient, type WaitlistStatus } from '@emr/shared';

import {
  createWaitlistEntry,
  listWaitlist,
  updateWaitlistEntryStatus,
  waitlistErrorMessage,
  type WaitlistEntry,
} from '../api/waitlist.ts';
import {
  Badge,
  Button,
  ErrorText,
  Field,
  Panel,
  SelectField,
  TextAreaField,
} from '../components/primitives.tsx';
import { useDateTimeFormat } from '../format/dateTimeFormatContext.ts';
import { formatDate } from '../format/formatDateTime.ts';
import { fromDatetimeLocalValue } from './datetimeLocalInput.ts';
import { PatientPicker } from './PatientPicker.tsx';
import { usePatient } from './usePatient.ts';
import { useOrgMembers } from './useOrgMembers.ts';

/** The "any provider" option's value in the add-form's provider select — distinct from any real user
 * id, matching `SchedulePage.tsx`'s `ALL_PROVIDERS` sentinel for the same reason: a UUID always
 * contains hyphens in fixed positions, this never will. */
const ANY_PROVIDER = 'any';

/** Every filter the "show" select offers — the three real statuses plus "every status at all". */
const STATUS_FILTERS = ['waiting', 'matched', 'expired', 'all'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

/** Narrows a `<select>`'s own string value back to `StatusFilter` — the options rendered below are
 * exactly `STATUS_FILTERS`, so this can never actually fail, but a native `<select>`'s `value` is
 * typed as plain `string` and the alternative is an unchecked cast (`claude-kit/rules/invariants.md`'s
 * "No type escape hatches"). */
function isStatusFilter(value: string): value is StatusFilter {
  return (STATUS_FILTERS as readonly string[]).includes(value);
}

function statusBadgeTone(status: WaitlistStatus): 'accent' | 'success' | 'neutral' {
  switch (status) {
    case 'waiting':
      return 'accent';
    case 'matched':
      return 'success';
    case 'expired':
      return 'neutral';
  }
}

/**
 * A patient's name via `usePatient`, matching `AppointmentList.tsx`'s own `PatientCell` — a separate,
 * file-local copy rather than importing that one, since it is not exported and this is only the
 * second use of the same three-line shape (`claude-kit/rules/invariants.md`'s "favor the general,
 * reusable solution once a pattern actually recurs" reserves extraction for a third occurrence).
 */
function PatientName({ patientId }: { readonly patientId: string }) {
  const { t } = useTranslation('schedule');
  const patient = usePatient(patientId);
  if (patient.data === undefined) {
    return (
      <span className="text-ink-muted">
        {patient.isError ? t('list.unknownPatient') : t('list.loading')}
      </span>
    );
  }
  return (
    <span className="font-medium text-ink">
      {patient.data.lastName}, {patient.data.firstName}
    </span>
  );
}

function ProviderName({ providerId }: { readonly providerId: string | null }) {
  const { t } = useTranslation('schedule');
  const members = useOrgMembers();
  if (providerId === null) return <span>{t('waitlist.anyProvider')}</span>;
  const member = members.data?.find((candidate) => candidate.userId === providerId);
  return <span>{member?.email ?? providerId}</span>;
}

function WaitlistRow({ entry }: { readonly entry: WaitlistEntry }) {
  const { t } = useTranslation('schedule');
  const { preference } = useDateTimeFormat();
  const queryClient = useQueryClient();

  const setStatus = useMutation({
    mutationFn: (status: WaitlistStatus) => updateWaitlistEntryStatus(entry.id, status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });

  return (
    <li className="rounded-control border border-line p-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <PatientName patientId={entry.patientId} />
        <Badge tone={statusBadgeTone(entry.status)}>{t(`waitlist.status.${entry.status}`)}</Badge>
      </div>
      <p className="m-0 mt-1 text-ink-muted">
        {t('waitlist.desiredWindow', {
          from: formatDate(entry.desiredFrom, preference),
          to: formatDate(entry.desiredTo, preference),
        })}
      </p>
      <p className="m-0 mt-0.5 text-ink-muted">
        <ProviderName providerId={entry.providerId} />
      </p>
      {entry.notes !== null && entry.notes.length > 0 ? (
        <p className="m-0 mt-1 text-ink">{entry.notes}</p>
      ) : null}
      {entry.status === 'waiting' ? (
        <div className="mt-2 flex flex-wrap gap-2">
          <Button
            variant="secondary"
            pending={setStatus.isPending && setStatus.variables === 'matched'}
            onClick={() => {
              setStatus.mutate('matched');
            }}
          >
            {t('waitlist.markMatched')}
          </Button>
          <Button
            variant="secondary"
            pending={setStatus.isPending && setStatus.variables === 'expired'}
            onClick={() => {
              setStatus.mutate('expired');
            }}
          >
            {t('waitlist.markExpired')}
          </Button>
        </div>
      ) : null}
      {setStatus.isError ? <ErrorText message={waitlistErrorMessage(setStatus.error)} /> : null}
    </li>
  );
}

interface AddFormState {
  readonly providerId: string;
  readonly desiredFrom: string;
  readonly desiredTo: string;
  readonly notes: string;
}

const EMPTY_ADD_FORM: AddFormState = {
  providerId: ANY_PROVIDER,
  desiredFrom: '',
  desiredTo: '',
  notes: '',
};

function AddToWaitlistForm() {
  const { t } = useTranslation('schedule');
  const queryClient = useQueryClient();
  const members = useOrgMembers();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [form, setForm] = useState<AddFormState>(EMPTY_ADD_FORM);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: createWaitlistEntry,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['waitlist'] });
      setPatient(null);
      setForm(EMPTY_ADD_FORM);
      setFormError(null);
    },
  });

  function handleSubmit() {
    setFormError(null);
    if (patient === null) {
      setFormError(t('form.errors.choosePatient'));
      return;
    }
    const desiredFrom = fromDatetimeLocalValue(form.desiredFrom);
    const desiredTo = fromDatetimeLocalValue(form.desiredTo);
    if (desiredFrom === null || desiredTo === null) {
      setFormError(t('waitlist.errors.enterWindow'));
      return;
    }
    if (!endsAfterStarts(desiredFrom, desiredTo)) {
      setFormError(t('waitlist.errors.windowOrder'));
      return;
    }
    const notes = form.notes.trim();
    create.mutate({
      patientId: patient.id,
      providerId: form.providerId === ANY_PROVIDER ? undefined : form.providerId,
      desiredFrom,
      desiredTo,
      notes: notes.length > 0 ? notes : undefined,
    });
  }

  return (
    <div className="flex flex-col gap-3">
      <PatientPicker
        selected={patient}
        onSelect={setPatient}
        onClear={() => {
          setPatient(null);
        }}
      />

      {members.data === undefined ? (
        <p className="m-0 text-sm text-ink-muted">
          {members.isError ? t('page.couldNotLoadProviders') : t('page.loadingProviders')}
        </p>
      ) : (
        <SelectField
          label={t('waitlist.form.providerLabel')}
          value={form.providerId}
          onChange={(event) => {
            setForm((current) => ({ ...current, providerId: event.target.value }));
          }}
        >
          <option value={ANY_PROVIDER}>{t('waitlist.anyProvider')}</option>
          {members.data.map((member) => (
            <option key={member.userId} value={member.userId}>
              {member.email} ({member.role})
            </option>
          ))}
        </SelectField>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Field
          type="datetime-local"
          label={t('waitlist.form.desiredFromLabel')}
          value={form.desiredFrom}
          onChange={(event) => {
            setForm((current) => ({ ...current, desiredFrom: event.target.value }));
          }}
          className="flex-1"
        />
        <Field
          type="datetime-local"
          label={t('waitlist.form.desiredToLabel')}
          value={form.desiredTo}
          onChange={(event) => {
            setForm((current) => ({ ...current, desiredTo: event.target.value }));
          }}
          className="flex-1"
        />
      </div>

      <TextAreaField
        label={t('waitlist.form.notesLabel')}
        value={form.notes}
        onChange={(event) => {
          setForm((current) => ({ ...current, notes: event.target.value }));
        }}
      />

      {formError !== null ? <ErrorText message={formError} /> : null}
      {create.isError ? <ErrorText message={waitlistErrorMessage(create.error)} /> : null}

      <div>
        <Button
          pending={create.isPending}
          onClick={() => {
            handleSubmit();
          }}
        >
          {t('waitlist.form.submit')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Waitlists (`BACKLOG.md` K3): patients wanting an earlier slot than what was available at booking
 * time, matched by hand when a cancellation opens one up. A separate panel from the calendar/agenda
 * above — no automatic matching, no `appointmentId` link — see `db/schema/scheduling.ts`'s
 * `waitlistEntries` header for why.
 *
 * Deliberately a card list, not `AppointmentList.tsx`'s dual desktop-table/mobile-card pattern — a
 * waitlist entry has no unbounded column under the same pressure a wide appointments table does
 * (`invariants.md`'s table-column strategy is about a table with several bounded/unbounded columns
 * competing for width; a single-column card list has no such column to protect), and this repo's
 * `B10` mobile-table sweep is explicitly separate, scoped work this panel does not reach into.
 */
export function WaitlistPanel() {
  const { t } = useTranslation('schedule');
  const [filter, setFilter] = useState<StatusFilter>('waiting');

  const query = useQuery({
    queryKey: ['waitlist', filter],
    queryFn: ({ signal }: { signal: AbortSignal }) =>
      listWaitlist(filter === 'all' ? {} : { status: filter }, signal),
  });

  return (
    <Panel title={t('waitlist.panelTitle')}>
      <div className="flex flex-col gap-4">
        <AddToWaitlistForm />

        <div className="border-t border-line pt-4">
          <SelectField
            label={t('waitlist.filterLabel')}
            value={filter}
            onChange={(event) => {
              const { value } = event.target;
              if (isStatusFilter(value)) setFilter(value);
            }}
            className="max-w-xs"
          >
            {STATUS_FILTERS.map((status) => (
              <option key={status} value={status}>
                {t(`waitlist.filterOption.${status}`)}
              </option>
            ))}
          </SelectField>

          {query.data === undefined ? (
            query.isError ? (
              <ErrorText message={waitlistErrorMessage(query.error)} />
            ) : (
              <p className="m-0 mt-3 text-sm text-ink-muted">{t('waitlist.loading')}</p>
            )
          ) : query.data.length === 0 ? (
            <p className="m-0 mt-3 text-sm text-ink-muted">{t('waitlist.empty')}</p>
          ) : (
            <ul className="m-0 mt-3 flex list-none flex-col gap-3 p-0">
              {query.data.map((entry) => (
                <WaitlistRow key={entry.id} entry={entry} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </Panel>
  );
}
